import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
	Card,
	CircularProgress,
	Collapse,
	Fade,
	Icon,
	InputLabel,
	MenuItem,
	Modal,
	Select,
	Slide,
	Tooltip,
} from "@mui/material";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { getUproColor } from "utils/colors";
import { printComanda } from "utils/comanda";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";
import { LIMITE_CUENTA_CORRIENTE_FALLBACK } from "utils/cuentaCorrienteConfig";
import {
	detalleEsImprimible,
	detallePerteneceASeccion,
	detalleRequiereImpresionComanda,
	obtenerSeccionesInvolucradas,
} from "utils/seccionesComanda";
import { requiereDestinoExplicito, resolveDestinoForSede } from "../pedidoDestino";
import {
	cuentaCorrienteCubreRemanente,
	getVoucherPaymentBreakdown,
} from "../../utils/voucherPayment";

const isClienteAlumnoAcademica = (usuario = {}) =>
	String(usuario?.sistema || usuario?.appUsuario || "").trim() === "Alumnos" &&
	String(usuario?.tipoUser || usuario?.tipoUsuario || "").trim() === "Alumno";

const CODIGO_CONDICIONES_PAGO_CAMBIARON = "CONDICIONES_PAGO_CAMBIARON";

const esErrorCondicionesPagoCambiaron = (error) =>
	error?.graphQLErrors?.some(
		(item) => item?.extensions?.code === CODIGO_CONDICIONES_PAGO_CAMBIARON
	) || error?.message?.includes(CODIGO_CONDICIONES_PAGO_CAMBIARON);

const limpiarCodigoError = (message = "") =>
	String(message || "").replace(`${CODIGO_CONDICIONES_PAGO_CAMBIARON}:`, "").trim();

export default function ModalConfirmarPedido({
	open,
	handleClose,
	carrito,
	fechaPedido,
	handleCloseAll,
	onPedidoCreado,
	menusDelDia,
	horaReserva,
	turnoPedido,
	idTurnoSede,
	idFranjaTurno,
	paraRetirar,
	usuario,
	token,
	tipo,
	dondeLlevar,
	destino = null,
	idSede: idSedeProp = null,
	saldoCuentaCorrientePendienteActual = 0,
	limiteCuentaCorriente = LIMITE_CUENTA_CORRIENTE_FALLBACK,
	bloqueoCuentaCorrienteLimite = false,
	voucherSeleccionado = null,
	onVolverMedioPago,
	onCondicionesPagoCambiaron,
}) {
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState(false);
	const [habilitadoLimite, setHabilitadoLimite] = useState(true);
	const [file, setFile] = useState(null);
	const inputRef = useRef(null);
	const [medioDePago, setMedioDePago] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionMeta, setSubmissionMeta] = useState({
		requiereTransferencia: false,
		comprobanteEnAlta: false,
		splitCuentaCorriente: false,
	});
	const { handleSnackbar } = useContext(MessageManager);
	const { confirmar } = useContext(ConfirmarContext);
	const [loadingImpresion, setLoadingImpresion] = useState(false);
	const total = carrito.reduce((acc, item) => acc + item.precioCarta * item.cantidad, 0);
	const selectedVoucher = voucherSeleccionado?.elegible ? voucherSeleccionado : null;
	const usaVoucherSeleccionado = Boolean(selectedVoucher);
	const desglosePago = getVoucherPaymentBreakdown({
		subtotal: total,
		voucher: selectedVoucher,
		recargo: 0,
	});
	const totalAPagar = desglosePago.total;
	const idSede = String(idSedeProp || localStorage.getItem("idSede") || "1");
	const destinoPedido = resolveDestinoForSede({ idSede, destino });
	const altaCerradaNotificadaRef = useRef(false);
	const [msgError, setMsgError] = useState("");
	const cuentaCorrienteRestringidaPorPerfil = isClienteAlumnoAcademica(usuario);
	const esCuentaCorrienteSeleccionada = Number(medioDePago) === -1;
	const esTransferenciaSeleccionada = String(medioDePago) === "1";
	const esMercadoPagoSeleccionado = medioDePago === "MercadoPago";
	const esBilleteraSeleccionada = medioDePago === "4";
	const transferenciaDisponibleParaSplit = idSede === "1";
	const disponibleCuentaCorriente = useMemo(
		() =>
			Math.max(
				Number(limiteCuentaCorriente || LIMITE_CUENTA_CORRIENTE_FALLBACK) -
				Number(saldoCuentaCorrientePendienteActual || 0),
				0
			),
		[limiteCuentaCorriente, saldoCuentaCorrientePendienteActual]
	);
	const cuentaCorrienteCubreTotalSeleccionado = cuentaCorrienteCubreRemanente({
		disponible: disponibleCuentaCorriente,
		remanente: usaVoucherSeleccionado ? totalAPagar : total,
	});
	const cuentaCorrienteDisponibleParcial =
		!usaVoucherSeleccionado &&
		disponibleCuentaCorriente > 0 &&
		disponibleCuentaCorriente < Number(total || 0);
	const requiereTransferenciaPorSplitSeleccionado =
		esCuentaCorrienteSeleccionada &&
		transferenciaDisponibleParaSplit &&
		cuentaCorrienteDisponibleParcial;
	const montoTransferenciaSplit = Math.max(
		Number(total || 0) - Number(disponibleCuentaCorriente || 0),
		0
	);
	const cuentaCorrienteSinDisponible =
		cuentaCorrienteRestringidaPorPerfil ||
		bloqueoCuentaCorrienteLimite ||
		disponibleCuentaCorriente <= 0;
	const cuentaCorrienteBloqueadaSelector =
		(!usuario?.habilitadoCuentaCorriente && !usuario?.vip) ||
		!habilitadoLimite ||
		cuentaCorrienteSinDisponible ||
		(!cuentaCorrienteCubreTotalSeleccionado &&
			(usaVoucherSeleccionado || !transferenciaDisponibleParaSplit));
	const mostrarDatosTransferencia =
		idSede === "1" &&
		(esTransferenciaSeleccionada || requiereTransferenciaPorSplitSeleccionado);

	const { data: mediosDePago } = useQuery(
		gql`
      query mediosDePagoModalConfirmarPedido($dni: Int, $idUsuario: Int, $appUsuario: String) {
        mediosDePago(
          filter: { online: true }
          dni: $dni
          idUsuario: $idUsuario
          appUsuario: $appUsuario
        ) {
          id
          nombre
          cuotas
          cbu
          alias
          titular
          allowedDNIs
        }
      }`,
		{
			variables: {
				dni: Number(usuario?.dni),
				idUsuario: Number(usuario?.id || 0) || null,
				appUsuario: usuario?.sistema || usuario?.appUsuario || null,
			},
			skip: !open,
			cachePolicy: "no-cache",
			fetchPolicy: 'cache-and-network'
		}
	);

	const normalizeDni = (dni) => String(dni ?? "").trim();

	const getAllowedDNIs = (medio) => {
		if (Array.isArray(medio?.allowedDNIs)) return medio.allowedDNIs;
		if (typeof medio?.allowedDNIs === "string") {
			try {
				const parsed = JSON.parse(medio.allowedDNIs);
				return Array.isArray(parsed) ? parsed : [];
			} catch (e) {
				return [];
			}
		}
		return [];
	};

	const medioPagoHabilitadoParaUsuario = (medio) => {
		const allowedDNIs = getAllowedDNIs(medio);
		if (allowedDNIs.length === 0) return true;
		return allowedDNIs.some((dni) => normalizeDni(dni) === normalizeDni(usuario?.dni));
	};
	const mediosDePagoDisponibles = useMemo(
		() =>
			(mediosDePago?.mediosDePago || []).filter(
				(medio) => medioPagoHabilitadoParaUsuario(medio) && medio?.nombre !== "Voucher"
			),
		[mediosDePago?.mediosDePago, usuario?.dni]
	);

	const medioPagoSeleccionado = mediosDePagoDisponibles.find(
		(medio) => medio?.id?.toString() === medioDePago?.toString()
	);
	const medioPagoBilletera = mediosDePagoDisponibles.find(
		(medio) => medio?.id?.toString() === "4"
	);
	const puedeUsarPasarelaBilletera = medioPagoHabilitadoParaUsuario(medioPagoBilletera);
	const formatVoucherPreviewValue = (value, voucherTipo) =>
		voucherTipo === "MONTO"
			? `$ ${formatMoneyPunto(value || 0)}`
			: `${Number(value || 0).toLocaleString("es-AR")} unidades`;
	const getVoucherPreviewTypeLabel = (voucherTipo) =>
		voucherTipo === "MONTO" ? "Monto" : voucherTipo === "PRODUCTO" ? "Producto" : "Categoría";
	const getVoucherPreviewTarget = (voucher) =>
		voucher?.tipo === "PRODUCTO" ? voucher?.producto?.nombre : voucher?.tipo === "CATEGORIA" ? voucher?.categoria?.categoria : null;

	useQuery(
		gql`
      query checkMenuDelDia($idSede: ID!, $destino: String, $idMenuDelDia: ID!, $token: String) {
        checkMenu(idSede: $idSede, destino: $destino, idMenuDelDia: $idMenuDelDia, token: $token)
      }
    `,
		{
			skip:
				!open ||
				!idSede ||
				!turnoPedido ||
				!token ||
				(requiereDestinoExplicito(idSede) && !destinoPedido) ||
				(menusDelDia && menusDelDia.length === 0) ||
				(carrito || []).length === 0,
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
			errorPolicy: "all",
			variables: {
				idSede,
				destino: destinoPedido,
				idMenuDelDia: carrito.map(
					(producto) => menusDelDia?.find((menu) => menu.producto?.id === producto?.id)?.id
				)?.[0],
				turnoPedido: turnoPedido,
				token,
			},
			onError: (error) => {
				setHabilitadoLimite(false);
				//Detectar si el error.message contiene "Failed to fetch" y cambiar el mensaje
				if (error?.message.includes("Failed to fetch")) {
					setMsgError(
						"Error de conexión al verificar la disponibilidad del menú. Por favor, verificá tu conexión a internet."
					);
				} else {
					setMsgError(error?.message || "No se puede realizar el pedido por falta de disponibilidad");
				}
			},
		}
	);

	const [saveVentaOnline, { data: dataSave, loading: loadingSave, error: errorSave }] = useMutation(
		gql`
      mutation saveVentaOnline(
        $input: VentaOnlineInput!
        $usuario: UsuarioVentaOnlineInput
        $comprobantes: [Upload!]
        $token: String
        $idSede: ID!
        $destino: String
      ) {
        saveVentaOnline(
          input: $input
          usuario: $usuario
          comprobantes: $comprobantes
          token: $token
          idSede: $idSede
          destino: $destino
        ) {
          ... on Venta {
            id
            pedido {
              id
              detalles {
                id
                numeroComanda
                producto {
                  nombre
                  seccion
                  comanderas {
                    id
                    printName
                    habilitado
                    estadoConexion
                  }
                }
                cantidad
                impresionComanda
              }
            }
          }
          ... on MercadoPagoResponse {
            id
            idPedido
            total
            linkPago
            idPagoMP
            estado
          }
        }
      }
    `
	);

	const [generateLinkPagoOnline, { loading: loadingLinkPago }] = useMutation(
		gql`
      mutation generateLinkPagoOnline(
        $input: VentaOnlineInput!
        $token: String
        $idSede: ID!
        $destino: String
        $correo: String
      ) {
        generateLinkPagoOnline(
          input: $input
          token: $token
          idSede: $idSede
          destino: $destino
          correo: $correo
        ) {
          Token
          Hash
          AccessLink
        }
      }
    `
	);

	const [getSedes, { data: dataSedes }] = useLazyQuery(
		gql`
      query sedesToken($filter: JSON, $token: String!) {
        sedesToken(filter: $filter, token: $token) {
          id
          nombre
          cbu
          alias
          nombreFantasia
          nombreTitularCuenta
          mpHabilitado
        }
      }
    `
	);

	useEffect(() => {
		if (localStorage.getItem("idSede") && open) {
			getSedes({
				variables: { filter: { id: localStorage.getItem("idSede") }, token },
				fetchPolicy: "no-cache",
			});
		}
	}, [open]);

	const [marcarDetallesComoImpresos] = useMutation(
		gql`
      mutation marcarDetallesComoImpresos($idsDetalles: [ID!]!) {
        marcarDetallesComoImpresos(idsDetalles: $idsDetalles) {
          success
          message
          fechaImpresion
        }
      }
    `
	);

	useEffect(() => {
		if (open) {
			setFile(null);
			setSuccess(false);
			setError(false);
			setSubmissionMeta({
				requiereTransferencia: false,
				comprobanteEnAlta: false,
				splitCuentaCorriente: false,
			});
			altaCerradaNotificadaRef.current = false;

			// No seleccionar nada por defecto para forzar al usuario a elegir
			setMedioDePago("");
			setIsSubmitting(false);
		}
	}, [open, mediosDePago, idSede]);

	const notificarAltaCerrada = () => {
		if (altaCerradaNotificadaRef.current) return;
		altaCerradaNotificadaRef.current = true;
		if (typeof onPedidoCreado === "function") {
			onPedidoCreado();
		}
	};

	const handleCopy = (text) => {
		navigator.clipboard.writeText(text);
		handleSnackbar("Alias copiado al portapapeles", "success");
	};

	const getComentarioPedido = () => {
		if (tipo === "menu") {
			return paraRetirar
				? "Pedido para retirar (Debe traer recipiente)"
				: "Pedido para consumir en el lugar";
		} else {
			return dondeLlevar !== "" ? `Llevar a ${dondeLlevar}` : "Para consumir en comedor";
		}
	};

	const finalizarPedido = () => {
		if (isSubmitting || loadingSave || loadingImpresion || loadingLinkPago) {
			return;
		}
		setIsSubmitting(true);
		const carritoActivo = (carrito || []).filter((item) => !item.eliminado);
		if (
			esCuentaCorrienteSeleccionada &&
			cuentaCorrienteRestringidaPorPerfil
		) {
			handleSnackbar(
				"Los clientes de Alumnos con tipo de usuario Alumno no pueden comprar con cuenta corriente",
				"error"
			);
			setIsSubmitting(false);
			return;
		}
		if (esCuentaCorrienteSeleccionada && cuentaCorrienteSinDisponible) {
			handleSnackbar(
				"Rechazado: no tenés saldo disponible en cuenta corriente para iniciar el split o cubrir el pedido",
				"error"
			);
			setIsSubmitting(false);
			return;
		}
		if (usaVoucherSeleccionado && desglosePago.requierePagoComplementario) {
			if (!esCuentaCorrienteSeleccionada && !esTransferenciaSeleccionada) {
				handleSnackbar(
					"Seleccioná cuenta corriente o transferencia para pagar el remanente",
					"error"
				);
				setIsSubmitting(false);
				return;
			}
			if (esCuentaCorrienteSeleccionada && !cuentaCorrienteCubreTotalSeleccionado) {
				handleSnackbar(
					"La cuenta corriente no alcanza para cubrir todo el remanente; elegí transferencia",
					"error"
				);
				setIsSubmitting(false);
				return;
			}
		}
		if (
			esCuentaCorrienteSeleccionada &&
			!usaVoucherSeleccionado &&
			Number(total || 0) > Number(disponibleCuentaCorriente || 0) &&
			!transferenciaDisponibleParaSplit
		) {
			handleSnackbar(
				`Rechazado: no se puede superar el límite acumulado de cuenta corriente de $${formatMoneyPunto(
					limiteCuentaCorriente
				)}`,
				"error"
			);
			setIsSubmitting(false);
			return;
		}
		if (tipo === "menu" && !usuario?.vip) {
			const cantidadTotal = carritoActivo.reduce(
				(acc, item) => acc + Number(item?.cantidad || 0),
				0
			);
			if (carritoActivo.length !== 1 || cantidadTotal !== 1) {
				handleSnackbar("Para pedidos de menú solo se permite 1 unidad por pedido.", "error");
				setIsSubmitting(false);
				return;
			}
		}

		if (
			!usaVoucherSeleccionado &&
			medioPagoSeleccionado &&
			!medioPagoHabilitadoParaUsuario(medioPagoSeleccionado)
		) {
			handleSnackbar("No tenés permisos para usar este medio de pago", "error");
			setIsSubmitting(false);
			return;
		}

		if (requiereDestinoExplicito(idSede) && !destinoPedido) {
			handleSnackbar("Debe seleccionar una ubicación válida para la sede elegida", "error");
			setIsSubmitting(false);
			return;
		}

		if (esBilleteraSeleccionada) {
			generateLinkPagoOnline({
				variables: {
					idSede,
					destino: destinoPedido,
					correo: usuario?.email || "",
					input: {
						tipo: tipo === "menu" ? "Menú" : "Cafetería",
						nombre: usuario?.nombre,
						apellido: usuario?.apellido,
						nro_doc: usuario?.dni,
						appUsuario: usuario?.sistema,
						idUsuario: usuario?.id,
						tipoUsuario: usuario.tipoUser,
						detalles: carrito
							.filter((item) => !item.eliminado)
							.map((producto) => ({
								idProducto: producto?.id,
								cantidad: producto.cantidad,
								precio: producto.precioCarta,
								idMenuDelDia: menusDelDia?.find((menu) => menu.producto?.id === producto?.id)?.id,
								esElaborado: producto?.esElaborado || false,
							})),
						fechaPedido: dayjs(fechaPedido).format("YYYY-MM-DD"),
						horaReserva: horaReserva
							? dayjs(fechaPedido).format("YYYY-MM-DD") + " " + horaReserva
							: dayjs().format("YYYY-MM-DD HH:mm"),
						turnoReserva: tipo === "menu" ? turnoPedido : null,
						idTurnoSede: tipo === "menu" ? idTurnoSede : null,
						idFranjaTurno: tipo === "menu" ? idFranjaTurno : null,
						comentario: getComentarioPedido(),
						paraRetirar: paraRetirar,
					},
					token,
				},
			})
				.then((res) => {
					const accessLink = res.data?.generateLinkPagoOnline?.AccessLink;
					if (accessLink) {
						window.location.href = accessLink;
					} else {
						throw new Error("No se pudo generar el link de pago");
					}
				})
				.catch((err) => {
					console.error("Error al generar link de pago:", err);
					handleSnackbar(
						err.message || "Ha ocurrido un error al generar el link de pago",
						"error"
					);
					setError(true);
				})
				.finally(() => {
					setIsSubmitting(false);
				});
			return;
		}

		const medioPago =
			mediosDePagoDisponibles.find((medio) => Number(medio?.id) === Number(medioDePago)) ||
			{};
		const debeEnviarComprobanteConAlta =
			Boolean(file) &&
			((esTransferenciaSeleccionada || requiereTransferenciaPorSplitSeleccionado) &&
				idSede === "1");
		const submissionMetaActual = {
			requiereTransferencia:
				esTransferenciaSeleccionada || requiereTransferenciaPorSplitSeleccionado,
			comprobanteEnAlta: debeEnviarComprobanteConAlta,
			splitCuentaCorriente: requiereTransferenciaPorSplitSeleccionado,
		};
		setSubmissionMeta(submissionMetaActual);

		// Flujo normal con saveVentaOnline
		saveVentaOnline({
			variables: {
				idSede,
				destino: destinoPedido,
				usuario: {
					id: usuario?.id,
					nombre: usuario?.nombre,
					apellido: usuario?.apellido,
					dni: usuario?.dni,
					username: usuario?.username,
					email: usuario?.email,
					tipoUser: usuario?.tipoUser,
					sistema: usuario?.sistema,
					cliente: usuario?.cliente,
					cuentaUpro: usuario?.cuentaUpro,
					habilitadoCuentaCorriente: usuario?.habilitadoCuentaCorriente,
					clienteHabilitado: usuario?.clienteHabilitado,
					esBecado: usuario?.esBecado,
				},
				input: {
					nombre: usuario?.nombre,
					apellido: usuario?.apellido,
					cliente: `${usuario?.nombre} ${usuario?.apellido}`,
					nro_doc: usuario?.dni,
					pagos: usaVoucherSeleccionado
						? desglosePago.cubiertoCompletamente || esCuentaCorrienteSeleccionada
							? []
							: [
								{
									nombre: "Transferencia",
									cuotas: 1,
									totalPago: totalAPagar,
									recargo: 0,
									totalRecargo: 0,
									idMedioDePago: 1,
								},
							]
						: esCuentaCorrienteSeleccionada
							? requiereTransferenciaPorSplitSeleccionado
								? [
									{
										nombre: "Transferencia",
										cuotas: 1,
										totalPago: montoTransferenciaSplit,
										recargo: 0,
										totalRecargo: 0,
										idMedioDePago: 1,
									},
								]
								: []
							: esMercadoPagoSeleccionado
								? [
									{
										nombre: "Mercado Pago",
										cuotas: 1,
										totalPago: 0,
										recargo: 0,
										totalRecargo: 0,
										idMedioDePago: null,
									},
								]
								: [
									{
										nombre: medioPago?.nombre,
										cuotas: medioPago?.cuotas,
										totalPago: 0,
										recargo: 0,
										totalRecargo: 0,
										idMedioDePago: medioDePago,
									},
								],
					paraRetirar: paraRetirar,
					idVoucher: usaVoucherSeleccionado ? Number(selectedVoucher.id) : null,
					cantidad: usaVoucherSeleccionado ? Number(selectedVoucher.cantidadAplicable || 0) : null,
					tipoVoucher: usaVoucherSeleccionado ? selectedVoucher.tipo : null,
					cuentaCorriente: esCuentaCorrienteSeleccionada,
					montoCuentaCorriente:
						usaVoucherSeleccionado && esCuentaCorrienteSeleccionada
							? totalAPagar
							: null,
					splitCuentaCorriente: usaVoucherSeleccionado
						? false
						: requiereTransferenciaPorSplitSeleccionado,
					appUsuario: usuario?.sistema,
					idUsuario: usuario?.id,
					tipoUsuario: usuario.tipoUser,
					formaPago: !usaVoucherSeleccionado && esMercadoPagoSeleccionado ? medioDePago : undefined,
					detalles: carrito
						.filter((item) => !item.eliminado)
						.map((producto) => ({
							idProducto: producto?.id,
							cantidad: producto.cantidad,
							precio: producto.precioCarta,
							idMenuDelDia: menusDelDia?.find((menu) => menu.producto?.id === producto?.id)?.id,
							esElaborado: producto?.esElaborado || false,
						})),
					fechaPedido: dayjs(fechaPedido).format("YYYY-MM-DD"),
					horaReserva: horaReserva
						? dayjs(fechaPedido).format("YYYY-MM-DD") + " " + horaReserva
						: dayjs().format("YYYY-MM-DD HH:mm"),
					turnoReserva: tipo === "menu" ? turnoPedido : null,
					idTurnoSede: tipo === "menu" ? idTurnoSede : null,
					idFranjaTurno: tipo === "menu" ? idFranjaTurno : null,
					tipo: tipo === "menu" ? "Menú" : "Cafetería",
					comentario: getComentarioPedido(),
				},
				comprobantes: debeEnviarComprobanteConAlta ? [file] : undefined,
				token,
			},
		})
			.then(async (res) => {
				const response = res.data.saveVentaOnline;

				// Si la respuesta es de tipo MercadoPagoResponse, redirigir al link de pago
				if (response.__typename === "MercadoPagoResponse" && response.linkPago) {
					// Es una respuesta de Mercado Pago - abrir link inmediatamente
					console.log("Abriendo link de Mercado Pago:", response.linkPago);
					window.open(response.linkPago, "_blank");
					handleSnackbar("Redirigiendo a Mercado Pago para completar el pago", "info");
					setSuccess(true);
					return;
				}

				notificarAltaCerrada();
				setSuccess(true);
				handleSnackbar("Pedido confirmado", "success");
			})
			.catch(async (err) => {
				console.error("Error al confirmar el pedido:", err);
				if (esErrorCondicionesPagoCambiaron(err)) {
					setFile(null);
					if (inputRef.current) inputRef.current.value = "";
					setMedioDePago("");
					setSubmissionMeta({
						requiereTransferencia: false,
						comprobanteEnAlta: false,
						splitCuentaCorriente: false,
					});
					setError(false);
					try {
						await onCondicionesPagoCambiaron?.();
					} catch (refreshError) {
						console.error("No se pudo actualizar la cuenta corriente:", refreshError);
					}
					handleSnackbar(
						limpiarCodigoError(err?.message) ||
						"Las condiciones de pago cambiaron. Volvé a seleccionar el medio de pago.",
						"warning"
					);
					return;
				}
				//Detectar si el err.message contiene "Failed to fetch" y cambiar el mensaje
				if (err?.message?.includes("Failed to fetch")) {
					handleSnackbar(
						"Error de conexión al confirmar el pedido. Por favor, verificá tu conexión a internet.",
						"error"
					);
				} else {
					handleSnackbar(err?.message || "Ha ocurrido un error al confirmar el pedido", "error");
				}
				setError(true);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const handleIntentarFinalizarPedido = () => {
		const advertenciaComprobantePendiente =
			"No has subido ningún comprobante de pago, vas a poder subirlo luego pero el pedido no se confirmará hasta que lo subas. Si te demorás más de 30 minutos el pedido se anulará automáticamente. ¿Querés continuar?";

		if (requiereTransferenciaPorSplitSeleccionado) {
			const message = file
				? `Tu cuenta corriente cubre $${formatMoneyPunto(
					disponibleCuentaCorriente
				)} y el resto se registrará como transferencia por $${formatMoneyPunto(
					montoTransferenciaSplit
				)}. El pedido quedará en revisión hasta que administración apruebe esa transferencia. ¿Querés continuar?`
				: `Tu cuenta corriente cubre $${formatMoneyPunto(
					disponibleCuentaCorriente
				)} y el resto se registrará como transferencia por $${formatMoneyPunto(
					montoTransferenciaSplit
				)}. ${advertenciaComprobantePendiente}`;

			confirmar({
				title: "Confirmar split de pago",
				message,
				icon: "warning",
				func: () => {
					finalizarPedido();
				},
			});
			return;
		}

		if (!file && esTransferenciaSeleccionada) {
			confirmar({
				title: "Advertencia",
				message: advertenciaComprobantePendiente,
				icon: "warning",
				func: () => {
					finalizarPedido();
				},
			});
			return;
		}

		finalizarPedido();
	};
	return (
		<Modal
			aria-labelledby="transition-modal-title"
			aria-describedby="transition-modal-description"
			open={open}
			onClose={() => {
				if (!loadingSave && !loadingImpresion && !loadingLinkPago) {
					if (success || error) {
						handleCloseAll();
					} else {
						handleClose();
					}
				}
			}}
			closeAfterTransition
		>
			<Fade in={open}>
				<Card
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						maxHeight: "90%",
						width:
							success || error
								? { xs: "80%", sm: "35%", xxl: "25%" }
								: { xs: "90%", sm: "40%", xxl: "30%" },
						p: success || error ? 0 : 3,
						overflowY: "auto",
					}}
				>
					{success ? (
						<Slide in={open} direction="up" mountOnEnter unmountOnExit>
							<Card>
								<SoftBox
									bgColor={getUproColor("uproGreen")}
									p={3}
									display="flex"
									flexDirection="column"
									justifyContent="center"
									alignItems="center"
									textAlign="center"
								>
									<Icon
										fontSize="large"
										sx={{
											color: "#fff",
											animation: "shakes 1s",
											"@keyframes shakes": {
												"0%": {
													transform: "rotate(0deg)",
												},
												"25%": {
													transform: "rotate(10deg)",
												},
												"50%": {
													transform: "rotate(-20deg)",
												},
												"75%": {
													transform: "rotate(15deg)",
												},
												"100%": {
													transform: "rotate(-5deg)",
												},
											},
										}}
									>
										check_circle
									</Icon>
									<SoftTypography variant="h5" color="white" fontWeight="bold" mt={2}>
										{submissionMeta.requiereTransferencia && !submissionMeta.comprobanteEnAlta
											? "Pedido pendiente de pago"
											: submissionMeta.requiereTransferencia && submissionMeta.comprobanteEnAlta
												? "Pedido en revisión"
												: "Pedido confirmado"}
									</SoftTypography>
									<SoftTypography variant="h6" color="white" fontWeight="bold">
										Tu número de pedido es{" "}
										{dataSave?.saveVentaOnline?.pedido?.id || dataSave?.saveVentaOnline?.idPedido}
									</SoftTypography>
									{submissionMeta.requiereTransferencia && (
										<SoftTypography variant="caption" color="white" mt={1}>
											<SoftTypography variant="caption" color="white" fontWeight="regular">
												{submissionMeta.comprobanteEnAlta
													? "El comprobante de pago será revisado y una vez aprobado podrás retirar tu pedido."
													: "Recordá que deberás cargar el comprobante de pago en el detalle del pedido en los próximos 30 minutos o el pedido será anulado."}
											</SoftTypography>
										</SoftTypography>
									)}
									<SoftBox mt={2}>
										<SoftButton color="white" circular onClick={handleCloseAll}>
											<SoftTypography variant="button" color="uproGreen">
												CERRAR
											</SoftTypography>
										</SoftButton>
									</SoftBox>
								</SoftBox>
							</Card>
						</Slide>
					) : error ? (
						<Slide in={open} direction="up" mountOnEnter unmountOnExit>
							<Card>
								<SoftBox
									bgColor={getUproColor("uproPink")}
									p={3}
									display="flex"
									flexDirection="column"
									justifyContent="center"
									alignItems="center"
									textAlign="center"
								>
									<Icon
										fontSize="large"
										sx={{
											color: "#fff",
											animation: "shakes 1s",
											"@keyframes shakes": {
												"0%": {
													transform: "rotate(0deg)",
												},
												"25%": {
													transform: "rotate(10deg)",
												},
												"50%": {
													transform: "rotate(-20deg)",
												},
												"75%": {
													transform: "rotate(15deg)",
												},
												"100%": {
													transform: "rotate(-5deg)",
												},
											},
										}}
									>
										error
									</Icon>
									<SoftTypography variant="h5" color="white" fontWeight="bold" mt={2}>
										Error al confirmar el pedido
									</SoftTypography>
									<SoftTypography variant="caption" color="white" mt={1}>
										{/* Detectar si errorSave.message contiene "Failed to fetch" y cambiar el mensaje */}
										{errorSave?.message?.includes("Failed to fetch")
											? "Error de conexión al cargar el comprobante. Por favor, verificá tu conexión a internet."
											: errorSave?.message ||
											"Ha ocurrido un error al confirmar el pedido. Por favor, intent? nuevamente más tarde o comunicate con soporte."}
									</SoftTypography>
									<SoftBox mt={2}>
										<SoftButton color="white" circular onClick={handleClose}>
											<SoftTypography variant="button" color="primary">
												CERRAR
											</SoftTypography>
										</SoftButton>
									</SoftBox>
								</SoftBox>
							</Card>
						</Slide>
					) : !habilitadoLimite ? (
						<Slide in={open} direction="up" mountOnEnter unmountOnExit>
							<Card>
								<SoftBox
									bgColor={getUproColor("uproYellow")}
									p={3}
									display="flex"
									flexDirection="column"
									justifyContent="center"
									alignItems="center"
									textAlign="center"
								>
									<Icon
										fontSize="large"
										sx={{
											color: "#fff",
											animation: "shakes 1s",
											"@keyframes shakes": {
												"0%": {
													transform: "rotate(0deg)",
												},
												"25%": {
													transform: "rotate(10deg)",
												},
												"50%": {
													transform: "rotate(-20deg)",
												},
												"75%": {
													transform: "rotate(15deg)",
												},
												"100%": {
													transform: "rotate(-5deg)",
												},
											},
										}}
									>
										warning
									</Icon>
									<SoftTypography variant="h5" color="white" fontWeight="bold" mt={2}>
										Atencion
									</SoftTypography>
									<SoftTypography variant="h6" color="white" mt={1}>
										{msgError || "No hay disponibilidad para este menú."}
									</SoftTypography>
									<SoftBox mt={2}>
										<SoftButton color="white" circular onClick={handleClose}>
											<SoftTypography variant="button" color="secondary">
												CERRAR
											</SoftTypography>
										</SoftButton>
									</SoftBox>
								</SoftBox>
							</Card>
						</Slide>
					) : (
						<SoftBox>
							<SoftBox display="flex" justifyContent="space-between">
								<SoftBox>
									<SoftTypography variant="h6">Confirmar pedido</SoftTypography>
								</SoftBox>
								<SoftBox display="flex" justifyContent="space-between">
									<SoftBox>
										<Tooltip title="Cerrar" placement="top">
											<Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
												close
											</Icon>
										</Tooltip>
									</SoftBox>
								</SoftBox>
							</SoftBox>
							<SoftBox pt={3}>
								<SoftBox
									display="flex"
									flexDirection="column"
									justifyContent="space-between"
									gap={2}
								>
									<SoftBox>
										<Card
											sx={{
												width: "100%",
											}}
										>
											<SoftBox p={2}>
												<SoftBox display="flex" alignItems="center">
													<SoftBox mr={1} display="flex" alignItems="center">
														<Icon color="primary">event_available</Icon>
													</SoftBox>
													<SoftTypography variant="h6">
														Fecha del pedido&nbsp;
														<Tooltip
															placement="top"
															title="Fecha en la que retirarás el pedido. No podrás retirarlo ni antes de la fecha ni pasada la misma."
														>
															<Icon
																sx={{
																	verticalAlign: "middle",
																}}
																color="secondary"
															>
																help
															</Icon>
														</Tooltip>
													</SoftTypography>
												</SoftBox>
												<SoftBox mt={2}>
													<SoftTypography
														variant="h5"
														fontWeight="bold"
														textAlign="center"
														color="primary"
													>
														{dayjs(fechaPedido).format("DD/MM/YYYY")}
													</SoftTypography>
												</SoftBox>
											</SoftBox>
										</Card>
									</SoftBox>
									<Collapse in={usaVoucherSeleccionado} mountOnEnter unmountOnExit>
										<Card sx={{ width: "100%", border: "1px solid rgba(54, 176, 74, 0.35)" }}>
											<SoftBox p={2}>
												<SoftBox display="flex" alignItems="center" gap={1}>
													<Icon color="success">redeem</Icon>
													<SoftTypography variant="h6">Voucher/beneficio aplicado</SoftTypography>
												</SoftBox>
												<SoftTypography variant="h6" fontWeight="bold" mt={1}>
													{selectedVoucher?.nombreVoucher || selectedVoucher?.grupo?.nombre || "Voucher"}
												</SoftTypography>
												<SoftTypography variant="caption" color="secondary" display="block" mt={0.5}>
													{getVoucherPreviewTypeLabel(selectedVoucher?.tipo)}
													{getVoucherPreviewTarget(selectedVoucher)
														? `: ${getVoucherPreviewTarget(selectedVoucher)}`
														: ""}
												</SoftTypography>
												<SoftTypography variant="caption" color="secondary" display="block" mt={0.5}>
													Aplicación: {formatVoucherPreviewValue(selectedVoucher?.cantidadAplicable, selectedVoucher?.tipo)}
												</SoftTypography>
												<SoftTypography variant="button" fontWeight="bold" display="block" mt={1}>
													El voucher cubre ${formatMoneyPunto(desglosePago.descuento)}
												</SoftTypography>
												<SoftTypography variant="button" fontWeight="bold" display="block">
													Restante a pagar: ${formatMoneyPunto(totalAPagar)}
												</SoftTypography>
											</SoftBox>
										</Card>
									</Collapse>
									<Card sx={{ width: "100%", border: "1px solid rgba(16, 42, 91, 0.14)" }}>
										<SoftBox p={2} aria-live="polite">
											{[
												["Subtotal del pedido", desglosePago.subtotal],
												...(usaVoucherSeleccionado ? [["Voucher", -desglosePago.descuento]] : []),
												["Recargo", desglosePago.recargo],
												["Total a pagar", totalAPagar],
											].map(([label, value], index, rows) => (
												<SoftBox key={label} display="flex" justifyContent="space-between" gap={2} mt={index ? 0.75 : 0}>
													<SoftTypography variant={index === rows.length - 1 ? "button" : "body2"} fontWeight={index === rows.length - 1 ? "bold" : "regular"}>
														{label}
													</SoftTypography>
													<SoftTypography variant={index === rows.length - 1 ? "button" : "body2"} fontWeight="bold">
														{value < 0 ? "-" : ""}${formatMoneyPunto(Math.abs(value))}
													</SoftTypography>
												</SoftBox>
											))}
										</SoftBox>
									</Card>
									<SoftBox>
										<Card
											sx={{
												width: "100%",
											}}
										>
											<SoftBox p={2}>
												<SoftBox display="flex" alignItems="center">
													<SoftBox mr={1} display="flex" alignItems="center">
														<Icon color="primary">attach_money</Icon>
													</SoftBox>
													<SoftTypography variant="h6">{usaVoucherSeleccionado && desglosePago.requierePagoComplementario ? "Completar con otro medio de pago" : "Medio de pago"}</SoftTypography>
												</SoftBox>
												{desglosePago.cubiertoCompletamente && (
													<SoftBox mt={2} display="flex" alignItems="center" gap={1} role="status" sx={{ color: "#279c32" }}>
														<Icon>check_circle</Icon>
														<SoftTypography variant="button" fontWeight="bold">
															Pedido cubierto completamente con voucher
														</SoftTypography>
													</SoftBox>
												)}
												<SoftBox mt={2} display={desglosePago.cubiertoCompletamente ? "none" : "block"}>
													<Select
														fullWidth
														variant="outlined"
														value={medioDePago}
														onChange={(e) => {
															setMedioDePago(e.target.value);
														}}
													>
														{/* Transferencia: solo disponible para Villa Mercedes (id=1) */}
														{idSede === "1" &&
															mediosDePagoDisponibles
																.filter((medio) => !usaVoucherSeleccionado || Number(medio?.id) === 1)
																.map((medio) => (
																	<MenuItem key={medio?.id} value={medio?.id?.toString()}>
																		{medio.nombre}
																	</MenuItem>
																))}

														{/* Cuenta corriente: disponible para todas las sedes */}
														<MenuItem
															value={-1}
															disabled={cuentaCorrienteBloqueadaSelector}
														>
															Cuenta corriente
														</MenuItem>

														{/* Mercado Pago: solo disponible para sedes que NO sean Villa Mercedes */}
														{!usaVoucherSeleccionado && <MenuItem
															value={"MercadoPago"}
															disabled={
																idSede === "1" ||
																!dataSedes?.sedesToken?.[0]?.mpHabilitado ||
																!habilitadoLimite
															}
														>
															Mercado Pago
														</MenuItem>}
													</Select>
													{usaVoucherSeleccionado &&
														desglosePago.requierePagoComplementario &&
														!cuentaCorrienteCubreTotalSeleccionado && (
															<SoftBox mt={1.5} display="flex" alignItems="flex-start" gap={1} role="status" sx={{ color: "#a15c00" }}>
																<Icon>info</Icon>
																<SoftTypography variant="caption" fontWeight="bold">
																	La cuenta corriente no alcanza para cubrir todo el remanente. Elegí transferencia.
																</SoftTypography>
															</SoftBox>
														)}
													<Collapse
														in={requiereTransferenciaPorSplitSeleccionado}
														mountOnEnter
														unmountOnExit
													>
														<SoftBox
															mt={2}
															p={2}
															sx={{
																borderRadius: "0.75rem",
																backgroundColor: "rgba(255, 193, 7, 0.15)",
																border: "1px solid rgba(255, 193, 7, 0.45)",
															}}
														>
															<SoftTypography variant="button" fontWeight="bold">
																La cuenta corriente cubre hasta $
																{formatMoneyPunto(disponibleCuentaCorriente)}.
															</SoftTypography>
															<SoftTypography variant="body2" mt={0.5}>
																Deberá completar el pago con transferencia por $
																{formatMoneyPunto(montoTransferenciaSplit)} y el pedido no quedará
																operativo hasta su aprobación.
															</SoftTypography>
														</SoftBox>
													</Collapse>
													<Collapse in={mostrarDatosTransferencia} mountOnEnter unmountOnExit>
														<SoftBox display="flex" flexDirection="column" gap={2} mt={2}>
															<SoftBox>
																<InputLabel htmlFor="modulo">
																	<SoftTypography
																		component="label"
																		variant="caption"
																		fontWeight="bold"
																	>
																		Alias
																	</SoftTypography>
																</InputLabel>
																<SoftTypography variant="h6" fontWeight="bold" color="primary">
																	{dataSedes?.sedesToken?.[0]?.alias || "No disponible"}
																</SoftTypography>
															</SoftBox>
															{dataSedes?.sedesToken?.[0]?.cbu && (
																<SoftBox>
																	<InputLabel htmlFor="modulo">
																		<SoftTypography
																			component="label"
																			variant="caption"
																			fontWeight="bold"
																		>
																			CBU
																		</SoftTypography>
																	</InputLabel>
																	<SoftTypography variant="h6" fontWeight="bold" color="primary">
																		{dataSedes?.sedesToken?.[0]?.cbu || "No disponible"}
																	</SoftTypography>
																</SoftBox>
															)}
															<SoftBox>
																<InputLabel htmlFor="modulo">
																	<SoftTypography
																		component="label"
																		variant="caption"
																		fontWeight="bold"
																	>
																		Titular
																	</SoftTypography>
																</InputLabel>
																<SoftTypography variant="h6" fontWeight="bold" color="primary">
																	{dataSedes?.sedesToken?.[0]?.nombreTitularCuenta ||
																		"No disponible"}
																</SoftTypography>
															</SoftBox>
															<SoftButton
																circular
																color="uproGreen"
																onClick={() =>
																	handleCopy(
																		mediosDePagoDisponibles.find(
																			(medio) => medio?.id.toString() === medioDePago
																		)?.alias
																	)
																}
															>
																Copiar alias&nbsp;
																<Icon>content_copy</Icon>
															</SoftButton>
															<SoftButton
																circular
																color={file ? "uproGreen" : "uproYellow"}
																onClick={() => inputRef.current.click()}
															>
																{file
																	? file.name.length > 20
																		? file.name.substring(0, 20) + "..."
																		: file.name
																	: requiereTransferenciaPorSplitSeleccionado
																		? "Subir comprobante de transferencia"
																		: "Subir comprobante de pago"}
																&nbsp;
																<Icon>{file ? "done" : "file_upload"}</Icon>
															</SoftButton>
															<input
																ref={inputRef}
																type="file"
																style={{ display: "none" }}
																accept=".pdf, .jpg, .jpeg, .png"
																onChange={(e) => {
																	if (e.target.files[0]) {
																		if (e.target.files[0].size > 5000000) {
																			handleSnackbar("El archivo debe pesar menos de 5MB", "error");
																		} else if (
																			// Si no está en la lista de extensiones permitidas
																			![
																				"application/pdf",
																				"image/jpeg",
																				"image/jpg",
																				"image/png",
																			].includes(e.target.files[0].type)
																		) {
																			handleSnackbar(
																				"El archivo debe ser un PDF, JPG, JPEG o PNG",
																				"error"
																			);
																		} else {
																			setFile(e.target.files[0]);
																		}
																	}
																}}
															/>
														</SoftBox>
													</Collapse>
													<Collapse in={esMercadoPagoSeleccionado}>
														<SoftBox p={2}>
															<SoftTypography variant="body2">
																Cuando confime el pedido, será redirigido a Mercado Pago para
																completar el pago.
															</SoftTypography>
														</SoftBox>
													</Collapse>
													<Collapse in={esBilleteraSeleccionada && puedeUsarPasarelaBilletera}>
														<SoftBox p={2}>
															<SoftTypography variant="body2">
																Cuando confirme el pedido, será redirigido a la pasarela de pago
																para completar la transacción.
															</SoftTypography>
														</SoftBox>
													</Collapse>
												</SoftBox>
											</SoftBox>
										</Card>
									</SoftBox>
									<Slide in={open} direction="up" mountOnEnter unmountOnExit>
										<SoftBox>
											<Card
												sx={{
													border: "1px solid rgba(0, 0, 0, 0.12)",
													zIndex: 2,
												}}
											>
												<SoftBox
													display="flex"
													justifyContent="space-between"
													px={2}
													py={1}
													sx={{
														borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
													}}
												>
													<SoftBox display="flex" alignItems="center">
														<SoftBox mr={1} display="flex" alignItems="center">
															<Icon color="primary">shopping_cart</Icon>
														</SoftBox>
														<SoftTypography variant="h6" fontWeight="bold">
															Carrito
														</SoftTypography>
													</SoftBox>
												</SoftBox>
												{carrito.filter((item) => !item.eliminado).length === 0 && (
													<SinDatos
														icono="production_quantity_limits"
														titulo="No hay productos"
														mensaje="Agrega productos para continuar"
													/>
												)}
												<Collapse
													in={open}
													sx={{
														maxHeight: {
															xs: "100%",
															sm: "50vh",
														},
														overflowY: "auto",
													}}
												>
													{carrito.map((producto, index) => (
														<Slide
															in={!producto.eliminado}
															key={producto.uniqueId || index}
															direction="right"
															mountOnEnter
															unmountOnExit
														>
															<SoftBox>
																<SoftBox
																	px={2}
																	py={1}
																	sx={{
																		borderBottom:
																			index === carrito.length - 1 ||
																				producto.uniqueId ===
																				carrito.filter((item) => !item.eliminado).slice(-1)[0]
																					?.uniqueId
																				? "none"
																				: "1px solid rgba(0, 0, 0, 0.12)",
																		"&:hover": {
																			backgroundColor: "rgba(0, 0, 0, 0.04)",
																		},
																	}}
																>
																	<SoftBox display="flex" justifyContent="space-between" gap={2}>
																		<SoftBox display="flex" alignItems="center">
																			<SoftBox display="flex" alignItems="center" gap={1}>
																				<SoftBox>
																					<Tooltip
																						title={producto.categoriaProducto.categoria}
																						placement="top"
																					>
																						<Icon
																							sx={{
																								color: getUproColor("uproYellow"),
																								cursor: "pointer",
																							}}
																						>
																							restaurant_menu
																						</Icon>
																					</Tooltip>
																				</SoftBox>
																				<SoftBox display="flex" gap={1} alignItems="center">
																					<SoftTypography variant="h6">
																						{makeNiceText(producto.nombre)}
																					</SoftTypography>
																					<SoftBadge
																						color="uproYellow"
																						badgeContent={`x${producto.cantidad}`}
																					/>
																				</SoftBox>
																			</SoftBox>
																		</SoftBox>
																		<SoftBox
																			display="flex"
																			justifyContent="flex-end"
																			alignItems="center"
																			gap={2}
																		>
																			<SoftBox display="flex" alignItems="center" gap={2}>
																				<SoftBox
																					display="flex"
																					flexDirection="column"
																					alignItems="flex-end"
																				>
																					<SoftTypography
																						variant="h6"
																						color={"text"}
																						sx={{
																							whiteSpace: "nowrap",
																						}}
																					>
																						${" "}
																						{formatMoneyPunto(
																							producto.precioCarta * producto.cantidad
																						)}
																					</SoftTypography>
																					<SoftTypography
																						variant="caption"
																						color={"text"}
																						sx={{
																							whiteSpace: "nowrap",
																						}}
																					>
																						$ {formatMoneyPunto(producto.precioCarta)} c/u
																					</SoftTypography>
																				</SoftBox>
																			</SoftBox>
																		</SoftBox>
																	</SoftBox>
																</SoftBox>
															</SoftBox>
														</Slide>
													))}
												</Collapse>
											</Card>
											<Card
												sx={{
													borderTopLeftRadius: 0,
													borderTopRightRadius: 0,
													position: "sticky",
													marginTop: -2,
													zIndex: 1,
												}}
											>
												<SoftBox bgColor="primary">
													<SoftBox
														display="flex"
														justifyContent="center"
														bgColor="primary"
														p={2}
														pt={4}
													>
														<SoftTypography variant="h6" fontWeight="bold" color="white">
															Total del pedido: $ {formatMoneyPunto(total)}
														</SoftTypography>
													</SoftBox>
												</SoftBox>
											</Card>
										</SoftBox>
									</Slide>
								</SoftBox>
							</SoftBox>
							<SoftBox mt={2} display="flex" flexDirection="column" justifyContent="space-between" alignItems="stretch" gap={2}>
								{onVolverMedioPago ? (
									<SoftButton circular color="uproYellow" onClick={onVolverMedioPago} sx={{ flex: 1 }}>
										<Icon>arrow_back</Icon>&nbsp;Volver al medio de pago
									</SoftButton>
								) : null}
								<SoftButton
									circular
									fullWidth={!onVolverMedioPago}
									color="uproGreen"
									sx={onVolverMedioPago ? { flex: 1 } : undefined}
									disabled={
										loadingSave ||
										loadingImpresion ||
										loadingLinkPago ||
										isSubmitting ||
										(!medioDePago &&
											(!usaVoucherSeleccionado || desglosePago.requierePagoComplementario)) ||
										!habilitadoLimite
									}
									onClick={handleIntentarFinalizarPedido}
								>
									Confirmar pedido&nbsp;
									{loadingSave || loadingImpresion || loadingLinkPago ? (
										<CircularProgress size={15} color="inherit" />
									) : (
										<Icon>done</Icon>
									)}
								</SoftButton>
							</SoftBox>
						</SoftBox>
					)}
				</Card>
			</Fade>
		</Modal>
	);
}

ModalConfirmarPedido.propTypes = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
	carrito: PropTypes.array,
	fechaPedido: PropTypes.string,
	handleCloseAll: PropTypes.func,
	onPedidoCreado: PropTypes.func,
	usuario: PropTypes.object,
	menusDelDia: PropTypes.array,
	horaReserva: PropTypes.string,
	turnoPedido: PropTypes.string,
	idTurnoSede: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	idFranjaTurno: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	paraRetirar: PropTypes.bool,
	token: PropTypes.string,
	tipo: PropTypes.string,
	dondeLlevar: PropTypes.string,
	destino: PropTypes.string,
	idSede: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	saldoCuentaCorrientePendienteActual: PropTypes.number,
	limiteCuentaCorriente: PropTypes.number,
	bloqueoCuentaCorrienteLimite: PropTypes.bool,
	voucherSeleccionado: PropTypes.object,
	onVolverMedioPago: PropTypes.func,
	onCondicionesPagoCambiaron: PropTypes.func,
};
